import { useCallback, useContext, useMemo } from "react";

import {
  CreateSourceBrowserbaseContext,
  CreateSourceBrowserbaseContextShape,
} from "@/pages/sources/components/create/sources/browserbase/CreateSourceBrowserbaseProvider";
import {
  buildCreateSourceBrowserbaseRequest,
  getCreateSourceBrowserbaseApiKeyError,
  getCreateSourceBrowserbaseProjectIdError,
  getCreateSourceBrowserbaseUrlError,
  isCreateSourceBrowserbaseValid,
} from "@/pages/sources/components/create/sources/browserbase/utils";
import { getCreateSourceNameError } from "@/pages/sources/components/create/sources/utils";

import { ToastVariant } from "@/providers/toast/Toast";
import { useToast } from "@/providers/toast/useToast";

import { useCreateSourceMutation } from "@/api/mutations/sources";

import { CreateSourceBrowserbaseActionType } from "./actions";

export function useCreateSourceBrowserbaseContext(): CreateSourceBrowserbaseContextShape {
  const context = useContext(CreateSourceBrowserbaseContext);
  if (!context) {
    throw new Error(
      "useCreateSourceBrowserbaseContext must be used within a CreateSourceBrowserbaseProvider",
    );
  }
  return context;
}

export function useCreateSourceBrowserbase() {
  const { state, dispatch } = useCreateSourceBrowserbaseContext();
  const { mutateAsync: createBrowserbaseSourceMutation } = useCreateSourceMutation();
  const { showToast } = useToast();

  const save = useCallback(async () => {
    dispatch({ type: CreateSourceBrowserbaseActionType.SET_SHOULD_SHOW_ERRORS, payload: true });
    if (!isCreateSourceBrowserbaseValid(state)) return;
    await createBrowserbaseSourceMutation(
      buildCreateSourceBrowserbaseRequest(state.sourceName, state.configuration),
      {
        onSuccess: () => {
          showToast({
            variant: ToastVariant.SUCCESS,
            header: "Source created",
            subheader: "The source has been created successfully",
          });
        },
        onError: (error) => {
          showToast({
            variant: ToastVariant.ERROR,
            header: "Failed to create source",
            subheader: error instanceof Error ? error.message : "An unknown error occurred",
          });
        },
      },
    );
  }, [dispatch, state, createBrowserbaseSourceMutation, showToast]);

  const nameError = useMemo(() => getCreateSourceNameError(state), [state]);
  const urlError = useMemo(() => getCreateSourceBrowserbaseUrlError(state), [state]);
  const apiKeyError = useMemo(() => getCreateSourceBrowserbaseApiKeyError(state), [state]);
  const projectIdError = useMemo(() => getCreateSourceBrowserbaseProjectIdError(state), [state]);
  const isValid = useMemo(() => isCreateSourceBrowserbaseValid(state), [state]);

  return {
    state,
    dispatch,
    nameError,
    urlError,
    apiKeyError,
    projectIdError,
    isValid,
    save,
  };
}
